// M_Components/M_Filters/EquipmentCostDetailFilter.jsx
import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar } from 'primereact/calendar'
import './EquipmentCostDetailFilter.css'

const EMPTY_FORM = {
    prefix:         '',
    number:         '',
    cd:             '',
    move:           '',
    movementKey:    '',
    eqTypeId:       '',
    from:           '',
    to:             '',
    vessel:         '',
    voyage:         '',
    estimationDate: null,
    approvalDate:   null,
    serviceCode:    '',
    supplier:       '',
    supplierRef:    '',
    status:         '',
    checked:        '',
}

const FIELD_MAP = {
    prefix:         { field: 'Equipment.Prefix',              type: 'string'  },
    number:         { field: 'Equipment.Number',              type: 'int32'   },
    cd:             { field: 'Equipment.CheckDigit',          type: 'int32'   },
    move:           { field: 'Movement.MovementType.Code',    type: 'string'  },
    movementKey:    { field: 'Movement.MovementKey',          type: 'int32'   },
    eqTypeId:       { field: 'Equipment.EquipmentType.Code',  type: 'string'  },
    from:           { field: 'Movement.MovementFromCode',     type: 'string'  },
    to:             { field: 'Movement.MovementToCode',       type: 'string'  },
    vessel:         { field: 'Movement.Transport.Name',       type: 'string'  },
    voyage:         { field: 'Movement.VoyageNumber',         type: 'string'  },
    estimationDate: { field: 'EstimationDate',                type: 'date'    },
    approvalDate:   { field: 'ApprovedDate',                  type: 'date'    },
    serviceCode:    { field: 'ServiceTypeCode',               type: 'string'  },
    supplier:       { field: 'SupplierName',                  type: 'string'  },
    supplierRef:    { field: 'SupplierRef',                   type: 'string'  },
    status:         { field: 'Status',                        type: 'string'  },
    checked:        { field: 'Checked',                       type: 'boolean' },
}

const FIELDS = [
    { key: 'prefix',         label: 'Prefix'        },
    { key: 'number',         label: 'Number'        },
    { key: 'cd',             label: 'CD'            },
    { key: 'move',           label: 'Move'          },
    { key: 'movementKey',    label: 'Mov. Key'      },
    { key: 'eqTypeId',       label: 'Eq. Type ID'   },
    { key: 'from',           label: 'From'          },
    { key: 'to',             label: 'To'            },
    { key: 'vessel',         label: 'Vessel'        },
    { key: 'voyage',         label: 'Voyage'        },
    { key: 'estimationDate', label: 'Est. Date'     },
    { key: 'approvalDate',   label: 'Approval Date' },
    { key: 'serviceCode',    label: 'Service Code'  },
    { key: 'supplier',       label: 'Supplier'      },
    { key: 'supplierRef',    label: 'Supplier Ref'  },
    { key: 'status',         label: 'Status'        },
    { key: 'checked',        label: 'Checked'       },
]

const formatDate = (date) => {
    if (!date) return ''
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

const buildFilterString = (form) => {
    const parts = []

    Object.entries(FIELD_MAP).forEach(([key, { field, type }]) => {
        if (type === 'date') {
            const date = formatDate(form[key])
            if (date) parts.push(`${field} eq '${date}'`)
            return
        }

        if (type === 'boolean') {
            const val = form[key]?.trim().toLowerCase()
            if (val === 'true' || val === 'false') {
                parts.push(`${field} eq ${val}`)
            }
            return
        }

        const val = form[key]?.trim()
        if (val) {
            parts.push(type === 'int32'
                ? `${field} eq ${val}`
                : `contains(${field}, '${encodeURIComponent(val)}')`)
        }
    })

    return parts.join(' and ')
}

export const EquipmentCostDetailFilter = ({ onFilterChange }) => {
    const { t } = useTranslation()

    const [form, setForm] = useState(EMPTY_FORM)
    const [isCollapsed, setIsCollapsed] = useState(false)
    const [isDirty, setIsDirty] = useState(false)
    const [hasActiveFilters, setHasActiveFilters] = useState(false)

    const handleChange = useCallback((key, value) => {
        setForm(prev => ({ ...prev, [key]: value }))
        setIsDirty(true)
    }, [])

    const handleSearch = () => {
        const filterString = buildFilterString(form)
        onFilterChange(filterString)
        setIsDirty(false)
        setHasActiveFilters(!!filterString)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch()
    }

    const handleClear = () => {
        setForm(EMPTY_FORM)
        onFilterChange('')
        setIsDirty(false)
        setHasActiveFilters(false)
    }

    return (
        <div className="ecd-filter-panel">
            <div
                className={`ecd-filter-header ${isCollapsed ? 'collapsed' : ''}`}
                onClick={() => setIsCollapsed(p => !p)}
            >
                <div className="ecd-filter-header-left">
                    <i className='pi pi-filter' style={{ fontSize: '0.9rem', color: '#6c757d' }} />
                    <span className="ecd-filter-header-title">{t('Filters')}</span>
                    {hasActiveFilters && !isDirty && (
                        <span className="ecd-filter-active-badge">{t('Active')}</span>
                    )}
                    {isDirty && (
                        <span className="ecd-filter-dirty-badge">{t('Unsaved')}</span>
                    )}
                </div>
                <div className="ecd-filter-header-right">
                    {hasActiveFilters && (
                        <button
                            className="ecd-filter-clear-btn"
                            onClick={e => { e.stopPropagation(); handleClear() }}
                        >
                            {t('Clear')}
                        </button>
                    )}
                    <i
                        className={`pi ${isCollapsed ? 'pi-chevron-down' : 'pi-chevron-up'}`}
                        style={{ fontSize: '0.8rem', color: '#6c757d' }}
                    />
                </div>
            </div>

            {!isCollapsed && (
                <>
                    <div className="ecd-filter-body">
                        <div className="ecd-filter-fields">
                            {FIELDS.map(({ key, label }) => {
                                const { type } = FIELD_MAP[key]
                                const val = form[key]
                                return (
                                    <div key={key} className="ecd-filter-field">
                                        <label className="ecd-filter-label">{t(label)}</label>
                                        {type === 'date' ? (
                                            <Calendar
                                                value={val}
                                                onChange={e => handleChange(key, e.value)}
                                                dateFormat="yy-mm-dd"
                                                placeholder={t(label)}
                                                showIcon
                                                showButtonBar
                                                inputClassName={`ecd-filter-input ${val ? 'active' : ''}`}
                                                className="ecd-filter-calendar"
                                                panelClassName="ecd-filter-calendar-panel"
                                            />
                                        ) : (
                                            <input
                                                className={`ecd-filter-input ${val ? 'active' : ''}`}
                                                value={val}
                                                onChange={e => handleChange(key, e.target.value)}
                                                onKeyDown={handleKeyDown}
                                                placeholder={t(label)}
                                            />
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    <div className="ecd-filter-footer">
                        <button
                            className={`ecd-filter-search-btn ${isDirty ? 'dirty' : ''}`}
                            onClick={handleSearch}
                        >
                            <i className="pi pi-search" style={{ fontSize: '0.85rem' }} />
                            {t('Search')}
                        </button>
                    </div>
                </>
            )}
        </div>
    )
}